import { anyOf } from "./signals";

const DEFAULT_TIMEOUT = 20_000; // 20 seconds

const header = name => response => response.headers.get(name);

const contentTypeOf = header("Content-Type");

const handleResponse = async response => {
	const data = /json/i.test(contentTypeOf(response) || "")
		? response.json().catch(() => null)
		: response.text();

	return {
		ok: response.ok,
		status: response.status,
		statusText: response.statusText,
		headers: response.headers,
		data: await data,
	};
};

const content = (body, contentType) => {
	if (body == null) return { body: null, contentType: null };

	const calculatedContentType =
		// prettier-ignore
		contentType != null ? contentType :
		typeof body === "object" ? "application/json" :
		"text/plain";

	const calculatedBody =
		// prettier-ignore
		/json/i.test(calculatedContentType) ? JSON.stringify(body) :
		/text\/plain/i.test(calculatedContentType) ? `${String(body)}` :
		body;

	return {
		body: calculatedBody,
		contentType: calculatedContentType,
	};
};

const buildInit = (method, data, baseOptions, options) => {
	const headers = new Headers({
		...baseOptions?.headers,
		...options?.headers,
	});

	const { body, contentType } = content(data, headers.get("Content-Type"));

	if (contentType != null) {
		headers.set("Content-Type", contentType);
	} else {
		headers.delete("Content-Type");
	}

	const timeout = options?.timeout || baseOptions?.timeout;

	const shouldAddTimeout = timeout == null || (Number.isFinite(timeout) && timeout >= 0);

	return {
		...baseOptions,
		...options,
		method,
		headers: Object.fromEntries(headers.entries()),
		signal: anyOf(
			shouldAddTimeout ? AbortSignal.timeout(timeout || DEFAULT_TIMEOUT) : null,
			options?.signal,
			baseOptions?.signal,
		),
		body,
	};
};

const createHttpClient = (baseURL, baseOptions) => {
	const join = endpoint =>
		endpoint ? `${baseURL.replace(/\/+$/, "")}/${endpoint.replace(/^\/+/, "")}` : baseURL;

	const send = (url, method, body, options) => {
		return fetch(
			url, //
			buildInit(method, body, baseOptions, options),
		).then(handleResponse);
	};

	const get = (endpoint, options) => send(join(endpoint), "GET", null, options);

	const post = (endpoint, data, options) => send(join(endpoint), "POST", data, options);

	const put = (endpoint, data, options) => send(join(endpoint), "PUT", data, options);

	const del = (endpoint, options) => send(join(endpoint), "DELETE", null, options);

	return {
		get,
		post,
		put,
		delete: del,
	};
};

export default createHttpClient;
